/**
 * apps/web/src/lib/strings/it.ts
 *
 * Italian UI strings (primary locale, source of truth for the dictionary shape).
 * Every user-facing string lives here; `Strings` is derived from this object, and
 * the English dictionary (`./en.ts`) must satisfy the same shape, so a missing
 * translation is a compile error. NEVER hardcode UI copy in components - add a
 * key here.
 *
 * NOTE: per house style we never use the em-dash character; use a hyphen,
 * comma, or parentheses instead.
 *
 * `it` is intentionally NOT `as const`: that widens literal strings to `string`
 * so the English mirror can hold different copy while still being shape-checked.
 */

export const it = {
  app: {
    name: 'glarno',
    tagline: 'Il sistema operativo AI della tua impresa',
  },
  nav: {
    oggi: 'Oggi',
    clienti: 'Clienti',
    progetti: 'Progetti',
    soldi: 'Soldi',
    documenti: 'Documenti',
    messaggi: 'Messaggi',
    team: 'Team',
    report: 'Report',
    libreria: 'Libreria',
    tabella: 'Tabella',
    governance: 'Governance',
    automazioni: 'Automazioni',
    impostazioni: 'Impostazioni',
    abbonamento: 'Abbonamento',
    altro: 'Altro',
    logout: 'Esci',
    ariaPrimary: 'Navigazione principale',
    ariaQuick: 'Navigazione rapida',
    ariaAll: 'Tutte le sezioni',
  },
  common: {
    loading: 'Caricamento...',
    retry: 'Riprova',
    cancel: 'Annulla',
    confirm: 'Conferma',
    save: 'Salva',
    search: 'Cerca',
    empty: 'Nessun elemento da mostrare',
    errorTitle: 'Qualcosa e andato storto',
    errorGeneric: 'Si e verificato un errore. Riprova tra poco.',
    reload: 'Ricarica',
    updateAvailableTitle: 'Aggiornamento disponibile',
    updateAvailableBody: 'E disponibile una nuova versione di glarno. Ricarica per continuare.',
    comingSoon: 'In arrivo',
    comingSoonBody: 'Questa sezione e in costruzione e sara presto disponibile.',
  },
  auth: {
    loginTitle: 'Bentornato',
    loginSubtitle: 'Accedi al tuo spazio di lavoro glarno',
    emailLabel: 'Email',
    emailPlaceholder: 'nome@azienda.it',
    passwordLabel: 'Password',
    passwordPlaceholder: 'La tua password',
    submit: 'Accedi',
    submitting: 'Accesso in corso...',
    invalidCredentials: 'Email o password non corretti',
    sessionExpired: 'La sessione e scaduta, accedi di nuovo',
  },
  oggi: {
    title: 'Oggi',
    subtitle: 'Il tuo centro operativo: approvazioni, urgenze, AI in corso.',
    // Daily summary banner (fallback when the BI Agent summary is unavailable).
    summaryFallback: 'Ecco le azioni piu importanti per oggi.',
    summaryLabel: 'Sintesi giornaliera',
    generatedAt: 'Aggiornato',
    refresh: 'Aggiorna',
    // Section headers on the inbox feed.
    sectionApprovals: 'Azioni da approvare',
    sectionApprovalsDesc: 'Azioni AI pronte: approva e invia, modifica o ignora.',
    sectionUrgencies: 'Urgenze',
    sectionUrgenciesDesc: 'Fatture scadute, clienti silenziosi, task bloccati.',
    sectionDecisions: 'Da decidere',
    sectionDecisionsDesc: 'Riconciliazioni, classificazioni e follow-up che richiedono una scelta.',
    sectionAiInProgress: 'Lavoro AI in corso',
    sectionAiInProgressDesc: 'Attivita degli agenti in esecuzione, con stato e possibilita di stop.',
    quickCommandsTitle: 'Comandi rapidi',
    emptyFeedTitle: 'Tutto sotto controllo',
    emptyFeedDesc: 'Non ci sono azioni in sospeso. Le nuove proposte AI appariranno qui.',
    emptySectionApprovals: 'Nessuna azione da approvare.',
    emptySectionUrgencies: 'Nessuna urgenza al momento.',
    emptySectionDecisions: 'Niente da decidere ora.',
    emptySectionAiInProgress: 'Nessun lavoro AI in corso.',
    // Mock-feed notice (shown while the /oggi endpoint is not yet live).
    mockNotice: 'Dati dimostrativi: la sorgente Oggi non e ancora attiva.',
    // Quick-command sheets (Talk to AI / Record note).
    talkSubmit: 'Invia',
    talkLabel: 'Cosa vuoi fare?',
    talkPlaceholder: 'Es. Prepara un preventivo per il cliente Rossi',
    noteSubmit: 'Registra',
    noteLabel: 'Nota',
    notePlaceholder: 'Scrivi la nota qui...',
    draftPreviewLabel: 'Anteprima proposta',
    draftPreviewPlaceholder: 'Testo della proposta AI...',
  },
  // Action / Approval Card copy (docs/07-ux/action-cards.md).
  card: {
    why: 'Perche',
    contextUsed: 'Contesto usato',
    sourceOpenExternal: 'Apri nella fonte esterna',
    proposedActions: 'Azioni proposte',
    confidence: 'Affidabilita',
    risk: 'Rischio',
    riskLow: 'basso',
    riskMedium: 'medio',
    riskHigh: 'alto',
    categoryNeedsApproval: 'Da approvare',
    categoryUrgency: 'Urgenza',
    categoryNeedsDecision: 'Da decidere',
    categoryAiInProgress: 'AI in corso',
    // Primary actions.
    approveAndSend: 'Approva e invia',
    edit: 'Modifica',
    onlyCreateTask: 'Solo crea task',
    ignore: 'Ignora',
    // Urgency / decision / in-progress actions.
    openDetails: 'Vedi dettagli',
    cancel: 'Annulla',
    started: 'Avviato',
    estimated: 'Stima',
    // States after acting.
    approving: 'Invio in corso...',
    approved: 'Approvata e inviata',
    rejected: 'Ignorata',
    taskCreated: 'Task creato',
    actionFailed: 'Azione non riuscita. Riprova.',
    // Edit sheet (placeholder: full inline editor is a later phase).
    editNotAvailable: 'La modifica inline arriva a breve. Per ora puoi approvare o creare solo il task.',
    confirmIgnoreTitle: 'Ignorare questa azione?',
    confirmIgnoreDesc: 'La proposta AI verra scartata e non verra eseguita.',
    expiresIn: 'Scade tra',
  },
  // Quick-command chips (docs/07-ux/navigation-and-screens.md §4.3).
  quickCommands: {
    talkToAi: 'Parla con AI',
    createProject: 'Crea progetto',
    recordNote: 'Registra nota',
    uploadDocument: 'Carica documento',
    createQuote: 'Crea preventivo',
    writeEmail: 'Scrivi email',
    createTask: 'Crea task',
    soon: 'In arrivo',
  },
  // ── Soldi (Finance Control) ────────────────────────────────────────────────
  soldi: {
    tabInvoices: 'Fatture',
    tabMovements: 'Movimenti',
    tabExpenses: 'Spese',
    // Invoice summary.
    summaryPaid: 'Incassato',
    summaryUnpaid: 'Da incassare',
    summaryOverdue: 'Scaduto',
    salesInvoices: 'Fatture di vendita',
    purchaseInvoices: 'Fatture di acquisto',
    invoiceNumber: 'Numero',
    company: 'Cliente',
    amount: 'Importo',
    dueDate: 'Scadenza',
    status: 'Stato',
    noInvoices: 'Nessuna fattura',
    noInvoicesDesc: 'Le fatture di vendita e di acquisto compaiono qui.',
    // Movements + reconciliation.
    unmatchedMovements: 'Movimenti da riconciliare',
    unmatchedDesc: 'Movimenti bancari senza un documento collegato.',
    reconciled: 'Riconciliato',
    counterparty: 'Controparte',
    valueDate: 'Data valuta',
    proposeMatch: 'Proponi abbinamento',
    proposing: 'Analisi in corso...',
    suggestedMatch: 'Abbinamento suggerito',
    noConfidentMatch: 'Nessun abbinamento sicuro trovato. Carica o collega un documento.',
    confirmMatch: 'Conferma abbinamento',
    confirming: 'Conferma in corso...',
    matchConfirmed: 'Movimento riconciliato',
    matchInvoice: 'Fattura',
    matchExpense: 'Spesa',
    confidence: 'Affidabilita',
    autoConfirm: 'Abbinamento sicuro',
    needsApproval: 'Richiede conferma',
    noMovements: 'Nessun movimento',
    noMovementsDesc: 'Quando colleghi la banca via PSD2, i movimenti appaiono qui.',
    // Missing documents.
    missingDocsTitle: 'Documenti mancanti',
    missingDocsDesc: 'Movimenti sotto la soglia di sicurezza che richiedono un documento.',
    // Expenses.
    pendingExpenses: 'Spese da approvare',
    expenseDescription: 'Descrizione',
    category: 'Categoria',
    date: 'Data',
    approve: 'Approva',
    reject: 'Rifiuta',
    markPaid: 'Segna pagata',
    expenseApproved: 'Spesa approvata',
    expenseRejected: 'Spesa rifiutata',
    expensePaid: 'Spesa pagata',
    noExpenses: 'Nessuna spesa',
    noExpensesDesc: 'Le spese inviate dal team compaiono qui per approvazione.',
    actionFailed: 'Operazione non riuscita. Riprova.',
  },
  // Invoice + expense status labels.
  invoiceStatus: {
    draft: 'Bozza',
    sent: 'Inviata',
    paid: 'Pagata',
    overdue: 'Scaduta',
    cancelled: 'Annullata',
    void: 'Annullata',
  },
  expenseStatus: {
    pending: 'In attesa',
    approved: 'Approvata',
    rejected: 'Rifiutata',
    paid: 'Pagata',
  },
  // ── Documenti ──────────────────────────────────────────────────────────────
  documenti: {
    filterAll: 'Tutti',
    name: 'Nome',
    classification: 'Classificazione',
    size: 'Dimensione',
    uploaded: 'Caricato',
    noDocuments: 'Nessun documento',
    noDocumentsDesc: 'I documenti caricati e classificati dall AI compaiono qui.',
    noResults: 'Nessun documento per questo filtro',
    noResultsDesc: 'Prova a cambiare la classificazione selezionata.',
    unclassified: 'Da classificare',
  },
  documentClass: {
    contract: 'Contratto',
    invoice: 'Fattura',
    receipt: 'Ricevuta',
    report: 'Report',
    misc: 'Altro',
  },
  // ── Messaggi (Business Inbox) ──────────────────────────────────────────────
  messaggi: {
    threads: 'Conversazioni',
    unreadOnly: 'Solo non letti',
    all: 'Tutte',
    selectThread: 'Seleziona una conversazione',
    selectThreadDesc: 'Scegli un thread a sinistra per leggerlo e rispondere.',
    noThreads: 'Nessuna conversazione',
    noThreadsDesc: 'Email, WhatsApp e SMS dei tuoi clienti appaiono qui.',
    aiDraft: 'Bozza AI',
    aiDraftReady: 'Bozza pronta',
    aiDraftDesc: 'glarno ha preparato una risposta con il contesto del cliente.',
    generateDraft: 'Genera bozza AI',
    reviewDraft: 'Rivedi bozza',
    send: 'Invia',
    reply: 'Rispondi',
    replyPlaceholder: 'Scrivi una risposta...',
    back: 'Conversazioni',
    // Channel labels.
    channelEmail: 'Email',
    channelWhatsapp: 'WhatsApp',
    channelSms: 'SMS',
    channelCall: 'Chiamata',
    channelInternal: 'Nota interna',
    // API-gap notice: the /v1/conversations endpoint is not exposed yet.
    notLiveTitle: 'Inbox in arrivo',
    notLiveDesc:
      'La sorgente messaggi (/v1/conversations) non e ancora attiva. La schermata si collegherà automaticamente quando l endpoint sarà disponibile.',
  },
  sections: {
    clienti: { title: 'Clienti', subtitle: 'Aziende e contatti del tuo spazio di lavoro.' },
    progetti: { title: 'Progetti', subtitle: 'Progetti, task, preventivi e fatture.' },
    soldi: { title: 'Soldi', subtitle: 'Fatture, spese, movimenti bancari, riconciliazioni.' },
    documenti: { title: 'Documenti', subtitle: 'Archivio documenti e classificazione AI.' },
    messaggi: { title: 'Messaggi', subtitle: 'Inbox aziendale multicanale con bozze AI.' },
    team: { title: 'Team', subtitle: 'Utenti, ruoli e assegnazioni.' },
    report: { title: 'Report', subtitle: 'KPI operativi e insight generati dall AI.' },
    libreria: { title: 'Libreria', subtitle: 'I documenti generati dall AI, con le fonti citate.' },
    tabella: {
      title: 'Tabella AI',
      subtitle: 'Griglia di clienti con colonne calcolate dall AI, con le fonti citate.',
    },
    automazioni: { title: 'Automazioni', subtitle: 'Workflow, agenti AI e pacchetti di settore.' },
    governance: { title: 'Governance', subtitle: 'Registro attivita, sicurezza e conformita dello spazio di lavoro.' },
    impostazioni: { title: 'Impostazioni', subtitle: 'Abbonamento, intake email e preferenze dello spazio di lavoro.' },
    abbonamento: { title: 'Abbonamento', subtitle: 'Gestisci il tuo piano e la fatturazione.' },
  },
  // ── Clienti (Company + Contact center) ─────────────────────────────────────
  clienti: {
    searchPlaceholder: 'Cerca per nome azienda...',
    newCompany: 'Nuovo cliente',
    newCompanyTitle: 'Nuovo cliente',
    countOne: '1 azienda',
    countMany: (n: number): string => `${n} aziende`,
    emptyTitle: 'Nessun cliente',
    emptyDesc: 'Quando aggiungi aziende e contatti, compaiono qui.',
    emptySearchTitle: 'Nessun risultato',
    emptySearchDesc: 'Nessuna azienda corrisponde alla ricerca.',
    colName: 'Nome',
    colType: 'Tipo',
    colEmail: 'Email',
    colPhone: 'Telefono',
    colCountry: 'Paese',
    backToList: 'Clienti',
    // Detail tabs.
    tabTimeline: 'Timeline',
    tabContacts: 'Contatti',
    tabProjects: 'Progetti',
    tabDocuments: 'Documenti',
    tabInvoices: 'Fatture',
    // Identity card.
    identity: 'Anagrafica',
    fieldLegalName: 'Ragione sociale',
    fieldVat: 'Partita IVA',
    fieldFiscalCode: 'Codice fiscale',
    fieldEmail: 'Email',
    fieldPhone: 'Telefono',
    fieldWebsite: 'Sito web',
    fieldCountry: 'Paese',
    // Graph depth stat labels.
    statProjects: 'Progetti',
    statContacts: 'Contatti',
    statInvoices: 'Fatture',
    statDocuments: 'Documenti',
    // Tab empty states.
    contactsEmpty: 'Nessun contatto per questa azienda.',
    projectsEmpty: 'Nessun progetto per questa azienda.',
    documentsEmpty: 'Nessun documento collegato.',
    invoicesEmpty: 'Nessuna fattura per questa azienda.',
    addContact: 'Nuovo contatto',
    newContactTitle: 'Nuovo contatto',
    openProject: 'Apri progetto',
    // Create form labels (shared with the company form).
    formName: 'Nome azienda',
    formNamePlaceholder: 'Es. Rossi Costruzioni Srl',
    formLegalName: 'Ragione sociale',
    formVat: 'Partita IVA',
    formEmail: 'Email',
    formPhone: 'Telefono',
    formType: 'Tipo',
    createError: 'Impossibile creare il cliente. Riprova.',
    // Contact form.
    contactFirstName: 'Nome',
    contactLastName: 'Cognome',
    contactEmail: 'Email',
    contactPhone: 'Telefono',
    contactRole: 'Ruolo',
    contactRolePlaceholder: 'Es. Responsabile acquisti',
    contactCreateError: 'Impossibile creare il contatto. Riprova.',
  },
  companyType: {
    customer: 'Cliente',
    supplier: 'Fornitore',
    partner: 'Partner',
    prospect: 'Potenziale',
  },
  // ── Progetti (Project center) ──────────────────────────────────────────────
  progetti: {
    searchPlaceholder: 'Cerca per nome progetto...',
    newProject: 'Nuovo progetto',
    newProjectTitle: 'Nuovo progetto',
    countOne: '1 progetto',
    countMany: (n: number): string => `${n} progetti`,
    emptyTitle: 'Nessun progetto',
    emptyDesc: 'I progetti che crei, con task, preventivi e fatture, compaiono qui.',
    emptySearchTitle: 'Nessun risultato',
    emptySearchDesc: 'Nessun progetto corrisponde ai filtri.',
    filterAll: 'Tutti',
    colName: 'Nome',
    colStatus: 'Stato',
    colBudget: 'Budget',
    colDates: 'Periodo',
    backToList: 'Progetti',
    // Detail tabs.
    tabTasks: 'Task',
    tabQuote: 'Preventivo',
    tabDocuments: 'Documenti',
    tabInvoices: 'Fatture',
    tabTimeline: 'Timeline',
    // Header meta.
    noClient: 'Nessun cliente',
    budgetLabel: 'Budget',
    periodLabel: 'Periodo',
    marginLabel: 'Margine',
    revenueLabel: 'Ricavi',
    costLabel: 'Costi',
    // Tab empty states + actions.
    tasksEmpty: 'Nessun task in questo progetto.',
    addTask: 'Nuovo task',
    newTaskTitle: 'Nuovo task',
    quotesEmpty: 'Nessun preventivo-contratto per questo progetto.',
    addQuote: 'Nuovo preventivo',
    newQuoteTitle: 'Nuovo preventivo',
    documentsEmpty: 'Nessun documento collegato a questo progetto.',
    invoicesEmpty: 'Nessuna fattura per questo progetto.',
    // Create form.
    formName: 'Nome progetto',
    formNamePlaceholder: 'Es. Ristrutturazione sede cliente',
    formDescription: 'Descrizione',
    formClient: 'Cliente',
    formClientNone: 'Nessun cliente',
    formStatus: 'Stato',
    formBudget: 'Budget',
    formBudgetPlaceholder: '0.00',
    createError: 'Impossibile creare il progetto. Riprova.',
    // Task form.
    taskTitle: 'Titolo',
    taskTitlePlaceholder: 'Es. Inviare preventivo aggiornato',
    taskPriority: 'Priorita',
    taskStatus: 'Stato',
    taskCreateError: 'Impossibile creare il task. Riprova.',
    quoteTitleLabel: 'Titolo preventivo',
    quoteTitlePlaceholder: 'Es. Proposta fornitura 2026',
    quoteCreateError: 'Impossibile creare il preventivo. Riprova.',
  },
  projectStatus: {
    active: 'Attivo',
    paused: 'In pausa',
    completed: 'Completato',
    cancelled: 'Annullato',
  },
  taskStatusLabel: {
    todo: 'Da fare',
    in_progress: 'In corso',
    needs_input: 'Attende input',
    blocked: 'Bloccato',
    done: 'Completato',
    cancelled: 'Annullato',
  },
  taskPriorityLabel: {
    low: 'Bassa',
    medium: 'Media',
    high: 'Alta',
    urgent: 'Urgente',
  },
  quoteStatusLabel: {
    draft: 'Bozza',
    sent: 'Inviato',
    signed: 'Firmato',
    rejected: 'Rifiutato',
    expired: 'Scaduto',
  },
  timeline: {
    title: 'Timeline',
    empty: 'Nessuna attivita registrata.',
    loadMore: 'Carica altre attivita',
    loadingMore: 'Caricamento...',
  },
  // ── Passwordless start ──────────────────────────────────────────────────────
  start: {
    title: 'Inizia con glarno',
    subtitle: 'Inserisci la tua email: ti inviamo un link per entrare, senza password.',
    emailLabel: 'Email di lavoro',
    emailPlaceholder: 'nome@azienda.it',
    submit: 'Inviami il link',
    submitting: 'Invio in corso...',
    invalidEmail: 'Inserisci un indirizzo email valido.',
    haveAccount: 'Hai gia una password?',
    signInLink: 'Accedi',
    // Check-inbox confirmation state.
    sentTitle: 'Controlla la posta',
    sentBody: (email: string): string =>
      `Se ${email} puo accedere, trovi un link per entrare nella tua casella. Scade tra 15 minuti.`,
    sentHint: 'Non trovi l email? Controlla lo spam o richiedi un nuovo link.',
    resend: 'Invia di nuovo',
    useAnother: 'Usa un altra email',
    openEmailApp: 'Apri l app email',
  },
  // ── Magic-link landing (token verification) ────────────────────────────────
  magicLink: {
    verifying: 'Verifica del link in corso...',
    successTitle: 'Ci siamo',
    successBody: 'Accesso effettuato. Prepariamo il tuo spazio di lavoro.',
    errorTitle: 'Link non valido',
    errorBody:
      'Questo link di accesso non e valido o e scaduto. Richiedine uno nuovo per continuare.',
    requestNew: 'Richiedi un nuovo link',
    missingToken: 'Manca il token di accesso. Apri il link dalla tua email.',
  },
  // ── Onboarding wizard ──────────────────────────────────────────────────────
  onboarding: {
    skip: 'Salta',
    skipForNow: 'Salta per ora',
    continue: 'Continua',
    back: 'Indietro',
    finish: 'Vai al tuo spazio',
    optional: 'Facoltativo',
    recommended: 'Consigliato',
    quickWin: 'Vittoria rapida',
    minutes: (n: number): string => `${n} min`,
    // Aha-moment banner.
    ahaLabel: 'Il tuo primo traguardo',
    ahaText: 'Collega il primo strumento e vedi la prima azione gia pronta nel tuo Oggi.',
    progress: (done: number, total: number): string => `${done} di ${total} completati`,
    // Segment step.
    segmentTitle: 'Come lavori?',
    segmentSubtitle: 'Personalizziamo glarno sul tuo modo di lavorare. Puoi cambiarlo dopo.',
    segmentFreelance: 'Freelance / professionista',
    segmentFreelanceDesc: 'Lavoro per conto mio e gestisco tutto in autonomia.',
    segmentStudio: 'Studio / micro-impresa',
    segmentStudioDesc: 'Siamo un piccolo team con progetti e clienti ricorrenti.',
    segmentAgency: 'Agenzia / azienda',
    segmentAgencyDesc: 'Coordiniamo un team su piu clienti e progetti.',
    segmentOther: 'Altro',
    segmentOtherDesc: 'Voglio esplorare e capire cosa fa per me.',
    // Checklist step.
    checklistTitle: 'I tuoi primi passi',
    checklistSubtitle: 'Parti dalla vittoria rapida: il resto puo aspettare.',
    stepDone: 'Fatto',
    stepStart: 'Inizia',
    stepConnect: 'Collega',
    stepReview: 'Apri',
    stepSecure: 'Imposta',
    // Connect cards.
    connectTitle: 'Collega i tuoi strumenti',
    connectSubtitle:
      'Collega gli strumenti che usi gia. glarno conserva le chiavi in modo sicuro e cifrato.',
    yourTools: 'I tuoi strumenti',
    weHandle: 'Pensiamo a tutto noi',
    weHandleSubtitle:
      'Questi servizi sono inclusi: glarno li gestisce per te, non devi configurare nulla.',
    connectAction: 'Collega',
    connecting: 'Apertura in corso...',
    connected: 'Collegato',
    notReady: 'In arrivo',
    notReadyHint: 'Le chiavi di connessione sono in fase di attivazione. Torna a breve.',
    included: 'Incluso',
    // Quick-win reveal.
    quickWinTitle: 'Ecco la tua prima azione',
    quickWinSubtitle: 'glarno ha gia trovato qualcosa di utile per te.',
    quickWinSampleLabel: 'Anteprima card Oggi',
    quickWinCardCta: 'La vedrai nel tuo Oggi',
    quickWinEmptyCta: 'Continua',
    quickWinContinue: 'Perfetto, continua',
    quickWinNothing: 'Tutto in ordine qui. Andiamo avanti.',
    // Set-password step.
    passwordTitle: 'Proteggi il tuo account',
    passwordSubtitle:
      'Imposta una password per accedere quando vuoi, anche senza link. Ultimo passo.',
    passwordLabel: 'Scegli una password',
    passwordPlaceholder: 'Almeno 8 caratteri',
    passwordConfirmLabel: 'Conferma password',
    passwordTooShort: 'La password deve avere almeno 8 caratteri.',
    passwordMismatch: 'Le due password non coincidono.',
    passwordSubmit: 'Imposta password e continua',
    passwordSaving: 'Salvataggio...',
    passwordSkip: 'Imposto la password dopo',
    passwordError: 'Impossibile salvare la password. Riprova.',
    // Errors.
    loadError: 'Impossibile caricare gli strumenti. Riprova.',
    authUrlError: 'Impossibile avviare la connessione. Riprova.',
  },
  // ── Impostazioni (workspace settings hub) ──────────────────────────────────
  impostazioni: {
    // Email-forward intake section.
    inboundTitle: 'Inoltro email con allegati',
    inboundDesc:
      'Inoltra qui le email con allegati: li archiviamo e indicizziamo in automatico.',
    inboundCopy: 'Copia indirizzo',
    inboundCopied: 'Indirizzo copiato',
    inboundCopyError: 'Copia non riuscita. Seleziona e copia manualmente.',
    inboundLoadError: 'Impossibile caricare l indirizzo di inoltro. Riprova.',
    inboundAddressLabel: 'Indirizzo di inoltro',
    // Language section.
    languageTitle: 'Lingua',
    languageDesc: 'Scegli la lingua dell interfaccia. Vale per tutto lo spazio di lavoro.',
    languageItalian: 'Italiano',
    languageEnglish: 'English',
    languageSaving: 'Salvataggio...',
    languageSaved: 'Lingua aggiornata',
    languageError: 'Impossibile aggiornare la lingua. Riprova.',
    // Appearance (light / dark / follow system).
    themeTitle: 'Aspetto',
    themeDesc: 'Scegli il tema chiaro, scuro o segui le impostazioni del sistema.',
    themeLight: 'Chiaro',
    themeDark: 'Scuro',
    themeSystem: 'Sistema',
    // Subscription section link.
    billingTitle: 'Abbonamento e fatturazione',
    billingDesc: 'Controlla il tuo piano, cambia abbonamento o gestisci i pagamenti.',
    billingOpen: 'Apri abbonamento',
    // Integrations section link.
    integrationsTitle: 'Integrazioni',
    integrationsDesc: 'Collega Gmail, Drive, Calendar e gli altri strumenti. Gestisci il connettore per Claude.',
    integrationsOpen: 'Apri integrazioni',
    // House-style AI preferences section (WorkspaceMemory).
    preferencesTitle: 'Preferenze AI',
    preferencesDesc:
      'Regole fisse che l assistente rispetta in ogni risposta: lingua, valuta e tono.',
    preferencesLangLabel: 'Lingua delle risposte',
    preferencesLangPlaceholder: 'es. italiano',
    preferencesCurrencyLabel: 'Valuta',
    preferencesCurrencyPlaceholder: 'es. EUR',
    preferencesToneLabel: 'Tono',
    preferencesTonePlaceholder: 'es. formale',
    preferencesSave: 'Salva',
    preferencesSaving: 'Salvataggio...',
    preferencesSaved: 'Preferenza salvata',
    preferencesError: 'Impossibile salvare la preferenza. Riprova.',
    preferencesRemoveError: 'Impossibile rimuovere la preferenza. Riprova.',
    preferencesClear: 'Rimuovi',
    preferencesInferredBadge: 'Suggerito dall AI',
    preferencesLoadError: 'Impossibile caricare le preferenze. Riprova.',
  },
  // ── Libreria (AI-authored deliverables archive) ────────────────────────────
  libreria: {
    filterAll: 'Tutti',
    filterBi: 'Analisi',
    filterDocument: 'Documenti',
    filterSales: 'Vendite',
    agentBi: 'Analisi',
    agentDocument: 'Documento',
    agentSales: 'Vendite',
    open: 'Apri',
    close: 'Chiudi',
    sourcesTitle: 'Fonti',
    sourcesNone: 'Nessuna fonte citata.',
    sourceOne: '1 fonte',
    sourceMany: (n: number): string => `${n} fonti`,
    delete: 'Elimina',
    deleteConfirm: 'Confermi eliminazione?',
    deleteError: 'Impossibile eliminare. Riprova.',
    detailError: 'Impossibile aprire il documento. Riprova.',
    loadError: 'Impossibile caricare la libreria. Riprova.',
    emptyTitle: 'La libreria e vuota',
    emptyDescription:
      'I documenti generati dall AI e approvati compaiono qui, con le loro fonti.',
  },
  // ── Tabella (AI table: clients x cited AI columns) ─────────────────────────
  tabella: {
    addColumn: 'Aggiungi colonna',
    runAll: 'Aggiorna tutto',
    stop: 'Ferma',
    colClient: 'Cliente',
    run: 'Calcola',
    retry: 'Riprova',
    computing: 'Elaborazione...',
    cellEmpty: 'Nessun dato',
    runColumn: 'Calcola colonna',
    removeColumn: 'Rimuovi colonna',
    searchPlaceholder: 'Cerca cliente...',
    emptyTitle: 'Nessun cliente',
    emptyDesc: 'Aggiungi clienti per costruire la tua tabella AI.',
    emptySearchTitle: 'Nessun risultato',
    emptySearchDesc: 'Nessun cliente corrisponde alla ricerca.',
    formTitle: 'Nuova colonna AI',
    formLabel: 'Titolo colonna',
    formLabelPlaceholder: 'Es. Ultimo contatto',
    formPrompt: 'Domanda AI',
    formPromptPlaceholder: 'Cosa deve calcolare l AI per ogni cliente?',
    formAgent: 'Agente',
    formSubmit: 'Aggiungi',
    agents: {
      inbox: 'Inbox',
      sales: 'Vendite',
      project: 'Progetti',
      finance: 'Finanza',
      document: 'Documenti',
      bi: 'Analisi',
    },
  },
  // ── Governance (audit trail + RBAC + EU compliance posture) ────────────────
  governance: {
    // Audit trail
    auditTitle: 'Registro attivita',
    auditSubtitle: 'Traccia immutabile di ogni modifica nello spazio di lavoro.',
    colWhen: 'Quando',
    colActor: 'Autore',
    colAction: 'Azione',
    colEntity: 'Oggetto',
    systemActor: 'Sistema',
    filterByEntity: 'Filtra per questo oggetto',
    filterByAction: 'Filtra per questa azione',
    activeFilters: 'Filtri attivi',
    clearFilter: 'Rimuovi filtro',
    clearFilters: 'Azzera filtri',
    pageWord: 'Pagina',
    totalLabel: 'voci',
    prev: 'Precedente',
    next: 'Successivo',
    emptyTitle: 'Nessuna attivita',
    emptyDesc: 'Le modifiche allo spazio di lavoro appariranno qui.',
    emptyFilteredTitle: 'Nessun risultato',
    emptyFilteredDesc: 'Nessuna voce corrisponde ai filtri attivi.',
    // Roles and permissions matrix
    matrixTitle: 'Ruoli e permessi',
    matrixSubtitle: 'Cosa puo fare ogni ruolo di sistema in questo spazio di lavoro.',
    matrixActionCol: 'Azione',
    matrixEmptyTitle: 'Nessun ruolo',
    matrixEmptyDesc: 'Non ci sono ruoli definiti in questo spazio di lavoro.',
    matrixNoAccess: 'Nessun accesso',
    matrixGranted: (scope: string): string => `Consentito con ambito ${scope}`,
    // Security and compliance posture
    complianceTitle: 'Sicurezza e conformita',
    complianceSubtitle: 'La postura di sicurezza e conformita della piattaforma per clienti UE.',
    items: {
      gdpr: {
        title: 'GDPR',
        desc: 'Trattamento dei dati conforme al Regolamento UE 2016/679.',
        status: 'Conforme',
      },
      aiAct: {
        title: 'EU AI Act',
        desc: 'Allineato al regolamento europeo sull intelligenza artificiale.',
        status: 'Allineato',
      },
      encryption: {
        title: 'Crittografia',
        desc: 'Dati cifrati in transito e a riposo.',
        status: 'Attivo',
      },
      noTraining: {
        title: 'Nessun addestramento',
        desc: 'I tuoi dati non addestrano modelli AI di terze parti.',
        status: 'Garantito',
      },
      residency: {
        title: 'Residenza dei dati',
        desc: 'Dati archiviati ed elaborati nell Unione Europea.',
        status: 'Attivo',
      },
      isolation: {
        title: 'Isolamento multi-tenant',
        desc: 'Ogni spazio di lavoro e isolato a livello applicativo e di database.',
        status: 'Attivo',
      },
    },
  },
  // ── Automazioni (workflow list + curated template library) ─────────────────
  automazioni: {
    // Tabs
    tabMine: 'I miei workflow',
    tabLibrary: 'Libreria',
    tablistLabel: 'Sezioni automazioni',
    // Workflow list
    listHeading: 'Workflow disponibili',
    emptyTitle: 'Nessun workflow',
    emptyDesc: 'Crea il primo workflow per automatizzare le operazioni ripetitive.',
    // Detail
    detailHeading: 'Dettaglio',
    detailPlaceholder: 'Seleziona un workflow per vedere i dettagli e avviarlo.',
    stepsHeading: 'Step del workflow',
    stepsEmpty: 'Nessuno step definito.',
    stepFallback: (n: number): string => `Step ${n}`,
    approvalBadge: 'Approvazione',
    approvalTitle: 'Richiede approvazione manuale',
    // Step type labels (for template-instantiated steps that carry only a type)
    stepTypeAiAction: 'Azione AI',
    stepTypeRequireApproval: 'Approvazione manuale',
    stepTypeSendNotification: 'Notifica',
    stepTypeCallIntegration: 'Integrazione',
    stepTypeCreateTask: 'Crea attivita',
    stepTypeCondition: 'Condizione',
    stepTypeDelay: 'Attesa',
    // Run action
    run: 'Avvia',
    running: 'Avvio in corso...',
    runSuccess: 'Workflow avviato con successo.',
    runError: 'Impossibile avviare il workflow. Riprova.',
    latestRunHeading: 'Ultima esecuzione avviata',
    // Run card
    currentStep: (step: string): string => `Passo corrente: ${step}`,
    startedAt: (rel: string): string => `Avviato ${rel}`,
    completedAt: (dt: string): string => `Completato ${dt}`,
    hideLog: 'Nascondi log',
    showLog: (n: number): string => `Mostra log (${n})`,
    // Recent runs
    runsThisWorkflow: 'Esecuzioni di questo workflow',
    runsRecent: 'Esecuzioni recenti',
    runsEmptyTitle: 'Nessuna esecuzione',
    runsEmptyDesc: 'Le esecuzioni dei workflow compaiono qui con stato e log.',
    // Step count
    stepCount: (n: number): string => `${n} step`,
    // Workflow status labels
    statusActive: 'Attivo',
    statusInactive: 'Inattivo',
    statusDraft: 'Bozza',
    statusPaused: 'In pausa',
    statusRunning: 'In corso',
    statusNeedsContext: 'Contesto richiesto',
    statusNeedsApproval: 'Approvazione richiesta',
    statusCompleted: 'Completato',
    statusFailed: 'Fallito',
    statusCancelled: 'Annullato',
    statusError: 'Errore',
    // Run status labels
    runStatusPending: 'In attesa',
    runStatusRunning: 'In corso',
    runStatusCompleted: 'Completato',
    runStatusFailed: 'Fallito',
    runStatusCancelled: 'Annullato',
    // Library tab
    libraryHeading: 'Libreria workflow',
    libraryDesc:
      'Modelli pronti all uso. Aggiungi un modello per creare una bozza di workflow nel tuo spazio.',
    libraryEmptyTitle: 'Nessun modello',
    libraryEmptyDesc: 'I modelli di workflow compariranno qui.',
    use: 'Usa',
    using: 'Creazione...',
    useSuccess: (name: string): string => `Bozza creata: ${name}. La trovi tra i tuoi workflow.`,
    useError: 'Impossibile creare il workflow dal modello. Riprova.',
    // Template category labels
    categoryProductivity: 'Produttivita',
    categoryFinance: 'Finanza',
    categorySales: 'Vendite',
    categoryOperations: 'Operazioni',
  },
  // ── Integrazioni (post-onboarding connector management) ────────────────────
  integrazioni: {
    title: 'Integrazioni',
    subtitle: 'Collega e gestisci le tue fonti dati e il connettore AI.',
    loadError: 'Impossibile caricare le integrazioni. Riprova.',
    connectedBanner: (provider: string): string => `${provider} collegato correttamente.`,
    connectFailedBanner: (provider: string): string =>
      `Collegamento a ${provider} non riuscito. Riprova.`,
    disconnectAction: 'Scollega',
    disconnecting: 'Scollegamento...',
    disconnectError: 'Impossibile scollegare. Riprova.',
    sectionConnectors: 'Fonti dati',
    sectionConnectorsDesc:
      'Ogni fonte collegata alimenta la knowledge base dello spazio di lavoro.',
    // MCP token card.
    mcpTitle: 'Connettore Claude (MCP)',
    mcpDesc:
      'Genera un link connettore e aggiungilo su Claude (Impostazioni > Connettori > Aggiungi connettore personalizzato) per interrogare questo spazio di lavoro.',
    mcpNameLabel: 'Nome del token',
    mcpNamePlaceholder: 'es. Claude di Marco',
    mcpCreate: 'Genera connettore',
    mcpCreating: 'Generazione...',
    mcpCreateError: 'Impossibile generare il token. Riprova.',
    mcpUrlLabel: 'URL del connettore (mostrato solo ora)',
    mcpUrlWarning:
      'Copialo subito: contiene la chiave segreta e non sara piu visibile. Trattalo come una password.',
    mcpCopy: 'Copia URL',
    mcpCopied: 'URL copiato',
    mcpCopyError: 'Copia non riuscita. Seleziona e copia manualmente.',
    mcpListTitle: 'Token attivi',
    mcpListEmpty: 'Nessun token generato.',
    mcpRevoke: 'Revoca',
    mcpRevoking: 'Revoca...',
    mcpRevoked: 'Revocato',
    mcpRevokeError: 'Impossibile revocare. Riprova.',
    mcpLastUsed: (when: string): string => `Ultimo uso: ${when}`,
    mcpNeverUsed: 'Mai usato',
  },
  // ── Abbonamento (billing / upgrade) ────────────────────────────────────────
  abbonamento: {
    // Current state card.
    currentPlanTitle: 'Il tuo piano',
    currentPlanNone: 'Periodo di prova',
    statusLabel: 'Stato',
    renewalLabel: 'Prossimo rinnovo',
    trialLabel: 'Prova fino al',
    cancelAtPeriodEnd: 'Il piano termina al prossimo rinnovo.',
    managedHint: 'Gestito tramite Stripe.',
    // Status vocabulary (Subscription.status).
    statusTrial: 'In prova',
    statusActive: 'Attivo',
    statusPastDue: 'Pagamento in ritardo',
    statusCancelled: 'Annullato',
    statusUnknown: 'Sconosciuto',
    // Plan picker.
    choosePlanTitle: 'Scegli il piano',
    choosePlanDesc: 'Passa a un piano superiore quando vuoi. Nessun vincolo.',
    intervalMonthly: 'Mensile',
    intervalYearly: 'Annuale',
    intervalYearlyHint: '2 mesi gratis',
    currentBadge: 'Piano attuale',
    upgradeAction: 'Aggiorna piano',
    starting: 'Apertura in corso...',
    // Manage (portal).
    manageTitle: 'Gestione abbonamento',
    manageDesc: 'Aggiorna il metodo di pagamento, scarica le fatture o annulla.',
    manageAction: 'Gestisci abbonamento',
    opening: 'Apertura in corso...',
    // Plan feature blurbs (concrete, not marketing fluff).
    planStarterName: 'Starter',
    planStarterDesc: 'Per chi inizia: clienti, progetti e fatture in un unico posto.',
    planProfessionalName: 'Professional',
    planProfessionalDesc: 'Automazioni AI, riconciliazioni e inbox multicanale.',
    planBusinessName: 'Business',
    planBusinessDesc: 'Team, ruoli avanzati e workflow di settore.',
    // Errors / states.
    paymentsNotConfigured: 'Il pagamento non e ancora attivo.',
    checkoutError: 'Impossibile avviare il pagamento. Riprova.',
    portalError: 'Impossibile aprire la gestione. Riprova.',
    loadError: 'Impossibile caricare lo stato dell abbonamento. Riprova.',
    // Return-from-Stripe banners (?stato=ok|annullato).
    returnOk: 'Pagamento completato. Aggiorniamo il tuo piano a breve.',
    returnCancelled: 'Pagamento annullato. Nessun addebito effettuato.',
  },
  // ── Consenso (signup terms + privacy) ──────────────────────────────────────
  consent: {
    // Rendered with the two policy links inserted around the label parts.
    labelPrefix: 'Accetto i ',
    termsLink: 'Termini',
    and: ' e la ',
    privacyLink: 'Privacy',
    required: 'Per continuare devi accettare i Termini e la Privacy.',
  },
};

export type Strings = typeof it;
